import { Notification } from '@basehub/data/replicache/index'
import * as React from 'react'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useUserStore } from '~/app/_store/user-store'
import { clsx } from 'clsx'
import { RingBellIcon } from '~/components/icons/ring-bell'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { NotificationItem } from './notification'
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover'
import { Button } from '~/sotano-kit/primitives/button'
import { PopoverHeader } from '~/sotano-kit/primitives/popover'

interface NotificationsBoxProps {
  notifications?: Notification[]
  isLoading?: boolean
}

export const NotificationsBox: React.FC<NotificationsBoxProps> = ({
  isLoading,
  notifications,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const currentUserId = useUserStore((s) => s.optimisticUser?.id)

  const { data } = useTeamReplicache()
  const teamReplicache = data?.replicache

  const ITEMS_PER_LOAD = 5

  const { readNotifications, unreadNotifications } = React.useMemo(() => {
    if (!notifications) {
      return { readNotifications: [], unreadNotifications: [] }
    }

    return notifications.reduce(
      (acc, notification) => {
        if (notification.read) {
          acc.readNotifications.push(notification)
        } else {
          acc.unreadNotifications.push(notification)
        }
        return acc
      },
      {
        readNotifications: [] as Notification[],
        unreadNotifications: [] as Notification[],
      }
    )
  }, [notifications])

  const [displayedReadCount, setDisplayedReadCount] =
    React.useState(ITEMS_PER_LOAD)

  const displayedReadNotifications = React.useMemo(() => {
    return readNotifications.slice(0, displayedReadCount)
  }, [readNotifications, displayedReadCount])

  const hasMoreRead = readNotifications.length > displayedReadCount

  const hasUnreadNotifications = unreadNotifications.length > 0

  const handleLoadMore = () => {
    setDisplayedReadCount((prevCount) =>
      Math.min(prevCount + ITEMS_PER_LOAD, readNotifications.length)
    )
  }
  if (!currentUserId) return null
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <IconButton
          aria-label="Notifications"
          className={clsx(
            'popover-trigger',
            isOpen && 'text-dark-bright bg-dark-base'
          )}
        >
          <RingBellIcon
            isHighlighted={hasUnreadNotifications}
            highlightClassName="text-dark-control-highlight"
          />
        </IconButton>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          forceMount
          align="end"
          ref={popoverRef}
          sideOffset={4}
          className={clsx(
            notifications && notifications.length > 2
              ? 'h-[clamp(300px,600px,90vh)]'
              : 'h-[clamp(300px,400px,90vh)]',
            'absolute right-0 top-full z-[70] flex flex-col min-w-[380px] max-w-[420px] bg-dark-base border border-dark-border-solid rounded-lg shadow-modal overflow-hidden',
            'transition-all duration-75 ease-in-out',
            'animate-popover-show'
          )}
        >
          <NotificationsBoxHeader
            hasUnreadNotifications={hasUnreadNotifications}
            markAllAsRead={async () => {
              if (!teamReplicache) {
                return
              }
              await teamReplicache.mutate.markAllNotificationsRead(
                currentUserId
              )
            }}
            title="Notifications"
          />
          <ScrollArea
            className={clsx(
              'flex-1 overflow-y-auto',
              notifications && notifications.length > 2
                ? 'min-h-[400px]'
                : 'h-auto min-h-full'
            )}
            viewportClassName="h-full"
          >
            {!isLoading && (!notifications || notifications.length === 0) && (
              <Placeholder />
            )}
            {notifications && notifications.length > 0 && (
              <div
                className={clsx(
                  'flex flex-col divide-y divide-dark-border-solid'
                )}
              >
                {/* Unread Notifications */}
                {unreadNotifications.length > 0 && (
                  <Separator isPrimary>New</Separator>
                )}
                {unreadNotifications.map((notification) => (
                  <NotificationItem
                    onRead={async () => {
                      if (!teamReplicache) {
                        return
                      }
                      await teamReplicache.mutate.markNotificationRead(
                        notification.id
                      )
                    }}
                    key={notification.id}
                    notification={notification}
                    currentUserId={currentUserId}
                  />
                ))}
                {/* Read Notifications */}
                {readNotifications.length > 0 && (
                  <Separator>Previous</Separator>
                )}
                {displayedReadNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    currentUserId={currentUserId}
                    onRead={() => {}} // Already read, so no action needed
                  />
                ))}
                {hasMoreRead && (
                  <div className="bg-dark-base-dark">
                    <Button
                      focusStyled
                      unstyled
                      onClick={handleLoadMore}
                      className="rounded-b-lg rounded-t-none !-outline-offset-1 w-full text-dark-faint text-xs text-left hover:text-dark-bright py-2 px-4 rounded hover:bg-dark-shade transition-colors cursor-default"
                    >
                      See previous notifications...
                    </Button>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}

const Separator = ({
  children,
  isPrimary,
}: React.PropsWithChildren & {
  isPrimary?: boolean
}) => {
  return (
    <div
      className={clsx(
        'px-4 py-2 text-dark-faint text-xs font-medium',
        isPrimary && '!text-dark-accent'
      )}
    >
      {children}
    </div>
  )
}

// ... (NotificationsBoxHeader and Placeholder components remain the same)

type NotificationsBoxHeaderProps = {
  title: string
  markAllAsRead: () => void
  hasUnreadNotifications: boolean
} & React.HTMLAttributes<HTMLDivElement>

const NotificationsBoxHeader: React.FC<NotificationsBoxHeaderProps> = ({
  className,
  title,
  markAllAsRead,
  hasUnreadNotifications,
  ...props
}) => {
  return (
    <PopoverHeader className={className} {...props}>
      <h2 className="text-dark-bright font-medium flex-1 text-sm">{title}</h2>
      <div className="flex gap-1.5 items-center">
        <Button
          className="text-dark-faint text-xs font-medium px-1.5 py-0.5 enabled:hover:bg-dark-shade enabled:hover:text-dark-bright rounded-md cursor-default"
          unstyled
          disabled={!hasUnreadNotifications}
          onClick={markAllAsRead}
        >
          {hasUnreadNotifications ? 'Mark all as read' : `You're up to date`}
        </Button>
      </div>
    </PopoverHeader>
  )
}

const Placeholder: React.FC = () => {
  return (
    <div className="h-full flex-1 flex flex-col items-center justify-center px-10 py-3 min-h-[250px]">
      <figure className="size-10 bg-dark-shade rounded-full flex items-center justify-center">
        <RingBellIcon isHighlighted={false} className="text-dark-dim size-6" />
      </figure>
      <p className="text-dark-muted mt-2 text-sm text-center">
        @tags, commits, content requests, and other relevant notifications will
        be displayed here
      </p>
    </div>
  )
}
